import React, { useCallback } from "react";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { PaymentMethod } from "../../../../common/enums";
import { useGetCountryCodesQuery } from "../../../../graphql/supergraph.index";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import { AccountDetails } from "./AccountDetails";
import { BankDetails } from "./BankDetails";
import { WirePaymentPrefInputSchema, type WirePaymentPrefInputType } from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const WirePaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Back",
}) => {
  const { setPaymentPrefDetail, workflowMode } = useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  /**
   * Validation relies on the country query hydrating the cache, so we block submission until
   * the country query resolves. [code-search: VALIDATED_COUNTRY_HYDRATE]
   */
  const { loading: countryLoading } = useGetCountryCodesQuery();

  const handleFormSubmission = useCallback(
    (values: WirePaymentPrefInputType) => {
      setPaymentPrefDetail(mapFormStateToContextState(values));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick],
  );

  if (loading || countryLoading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.Wire}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "WirePaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
      )}
      validationSchema={WirePaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <BankDetails />
      <AccountDetails />
    </PaymentPrefLayoutForm>
  );
};
